import { Link } from "react-router-dom";
import {
  CALL_REQUEST_KIND_LABELS,
  REQUEST_SMS_STATUS_LABELS,
  type CallRequest,
} from "@shared/modules/x-appels";
import { formatDateTime } from "../../../core/lib/format";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { atelierEnabled } from "../lib/api";
import { PhoneLinks } from "./phone-links";

/**
 * Une demande prise par Léo : rappel ou annulation, avec ce qu'il faut pour
 * agir (nom, numéro, rendez-vous, raison) et l'état du texto au propriétaire.
 * Léo n'annule jamais lui-même : la demande reste « à traiter » tant que
 * l'équipe n'a pas rappelé le client.
 */
export function RequestCard({
  request,
  busy,
  onHandled,
  onResend,
  compact,
}: {
  request: CallRequest;
  busy?: boolean;
  onHandled?: (r: CallRequest) => void;
  onResend?: (r: CallRequest) => void;
  compact?: boolean;
}) {
  const pending = request.status === "a_traiter";
  const smsFailed = request.smsStatus === "echec";
  const smsVariant =
    request.smsStatus === "envoye" || request.smsStatus === "simule"
      ? "success"
      : smsFailed
        ? "danger"
        : "neutral";
  return (
    <article
      className={`request-card${pending ? " request-card--pending" : ""}${request.kind === "annulation" ? " request-card--cancel" : ""}`}
    >
      <div className="request-card__head">
        <Badge variant={request.kind === "annulation" ? "warning" : "primary"}>
          {CALL_REQUEST_KIND_LABELS[request.kind]}
        </Badge>
        {!pending ? <Badge variant="neutral">Traitée</Badge> : null}
        <Badge variant={smsVariant}>{REQUEST_SMS_STATUS_LABELS[request.smsStatus]}</Badge>
        <span className="request-card__when">
          {formatDateTime(request.createdAt, { dateStyle: "medium", timeStyle: "short" })}
        </span>
      </div>
      <p className="request-card__who">
        <strong>{request.callerName ?? "Nom non donné"}</strong>
        <PhoneLinks number={request.phone} />
      </p>
      {request.appointmentLabel ? (
        <p className="request-card__rdv">
          <Icon name="calendar" size={14} /> RDV du {request.appointmentLabel}
          {atelierEnabled && request.appointmentId ? (
            <>
              {" · "}
              <Link to={`/travaux/${request.appointmentId}`}>Voir le rendez-vous</Link>
            </>
          ) : null}
          {request.kind === "annulation" ? (
            <span className="request-card__note">
              {" "}
              — reste à l’horaire tant que l’équipe n’a pas confirmé avec le client.
            </span>
          ) : null}
        </p>
      ) : request.kind === "annulation" ? (
        <p className="request-card__rdv">Rendez-vous à identifier au dossier du client.</p>
      ) : null}
      {request.reason ? <p className="request-card__reason">Raison : {request.reason}</p> : null}
      {smsFailed && request.smsError && !compact ? (
        <p className="request-card__error">Texto non envoyé : {request.smsError}</p>
      ) : null}
      <div className="request-card__actions">
        {atelierEnabled && request.clientId ? (
          <Link to={`/clients/${request.clientId}`} className="btn btn--ghost btn--sm">
            Fiche client
          </Link>
        ) : null}
        {request.callId && !compact ? (
          <Link to={`/appels/${request.callId}`} className="btn btn--ghost btn--sm">
            Écouter l’appel
          </Link>
        ) : null}
        {onResend && (smsFailed || request.smsStatus === "sans_destinataire") ? (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onResend(request)}>
            Renvoyer le texto
          </Button>
        ) : null}
        {onHandled && pending ? (
          <Button
            variant="success"
            size="sm"
            disabled={busy}
            icon={<Icon name="check" size={14} />}
            onClick={() => onHandled(request)}
          >
            Marquer traitée
          </Button>
        ) : null}
      </div>
    </article>
  );
}
